import { Query } from '@apollo/client/react/components';
import { filter, any, pathOr, pipe } from 'ramda';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjectVySida';
import { objektVyGrupp } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyGrupp';
import ObjectTable from '../../../_Common/Components/BusinessCollectionComponents/ObjectTable/ObjectTable';
import {
  IObjektVy,
  BusinessCollectionCompType,
} from '../../../../../Types/ObjektVy';
import { standardVy, objektLänk } from '../../../../../Utils/ObjektVyUtils';
import { dataAsNodeData } from '../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import KontoQuery from './Queries/KontoQuery/KontoQuery.gql';
import {
  KontoQuery as IKontoQueryData,
  KontoQueryVariables,
  KontoQuery_node_TEDEKBokfver__Associations_Konteringar as IKontering,
  KontoQuery_node_TEDEKBokfver__Associations_Kodplan__Associations_Kontodelsdef,
} from './Queries/KontoQuery/_QueryTypes/KontoQuery';
import { IColumnDef } from '../../../../../Types/ColumnDefType';
import { propOr } from 'ramda';
import { objektVyLista } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyLista';
import columnDefVy from '../../../_Common/Components/BusinessCollectionComponents/columnDefVy';
import { selectedId } from '../../../../../Components/MasterDetail/MasterDetailUtils';
import { findFormatter } from '../../../../../Utils/Formatters/FindFormatter';

const kontoKolumner: Array<IColumnDef & { koddel?: number }> = [
  {
    attributeDef: 'KBEN1',
    caption: 'Koddel 1',
    koddel: 1,
    cellTitleDef: 'KDEL1',
  },
  {
    attributeDef: 'KBEN2',
    caption: 'Koddel 2',
    cellTitleDef: 'KDEL2',
    koddel: 2,
  },
  {
    attributeDef: 'KBEN3',
    cellTitleDef: 'KDEL3',
    caption: 'Koddel 3',
    koddel: 3,
  },
  {
    attributeDef: 'KBEN4',
    cellTitleDef: 'KDEL4',
    caption: 'Koddel 4',
    koddel: 4,
  },
  {
    attributeDef: 'KBEN5',
    cellTitleDef: 'KDEL5',
    caption: 'Koddel 5',
    koddel: 5,
  },
  {
    attributeDef: 'KBEN6',
    cellTitleDef: 'KDEL6',
    caption: 'Koddel 6',
    koddel: 6,
  },
  {
    attributeDef: 'KBEN7',
    cellTitleDef: 'KDEL7',
    caption: 'Koddel 7',
    koddel: 7,
  },
  {
    attributeDef: 'KBEN8',
    cellTitleDef: 'KDEL8',
    caption: 'Koddel 8',
    koddel: 8,
  },
  {
    attributeDef: 'KBEN9',
    cellTitleDef: 'KDEL9',
    caption: 'Koddel 9',
    koddel: 9,
  },
  {
    attributeDef: 'KBEN10',
    cellTitleDef: 'KDEL10',
    caption: 'Koddel 10',
    koddel: 10,
  },
  { attributeDef: 'DEBET', caption: 'Debet' },
  { attributeDef: 'KREDIT', caption: 'Kredit' },
  { attributeDef: 'TEXT', caption: 'Text' },
];

const setKolumnBenamningar = (
  kontoKolumner: Array<IColumnDef & { koddel?: number }>,
  koddelsdata:
    | KontoQuery_node_TEDEKBokfver__Associations_Kodplan__Associations_Kontodelsdef[]
    | null
): IColumnDef[] =>
  koddelsdata
    ? kontoKolumner.map(col =>
        col.koddel
          ? {
              attributeDef: col.attributeDef,
              caption: propOr(
                'koddel saknas',
                'NAMN',
                koddelsdata.find(del => del.ORDNINGS_NR === col.koddel)
              ),
            }
          : col
      )
    : kontoKolumner;

const filtreraAnvändaKoddelar = (
  Konteringar: IKontering[],
  kontoKolumner: IColumnDef[]
): IColumnDef[] =>
  filter((column: IColumnDef) =>
    any(
      kontering =>
        Boolean(
          (kontering as unknown as Record<string, unknown>)[column.attributeDef]
        ),
      Konteringar
    )
  )(kontoKolumner);

// const rensaNollDebetKredit: (_: IKontering) => IKontering = evolve({
//   DEBET: x => (x === '0.00' ? null : x),
//   KREDIT: x => (x === '0.00' ? null : x),
// });

const setCellTitles =
  (kontoKolumner: Array<IColumnDef & { koddel?: number }>) =>
  (column: IColumnDef): IColumnDef => {
    const kontoKolumn: IColumnDef | undefined = kontoKolumner.find(
      col => col.attributeDef === column.attributeDef
    );

    if (kontoKolumn) {
      return { ...column, cellTitleDef: kontoKolumn.cellTitleDef };
    } else {
      return column;
    }
  };

const konteringscomp: BusinessCollectionCompType = props => (
  <Query<IKontoQueryData, KontoQueryVariables>
    query={KontoQuery}
    variables={{ ID: selectedId(props.id) || 'skipped id' }}
    skip={!props.id}
  >
    {({ data }) => {
      const nodeData = dataAsNodeData(data);
      const node =
        (data &&
          data.node &&
          data.node.__typename === 'TEDEKBokfver' &&
          data.node) ||
        null;
      const kontodelsdef =
        node &&
        node._Associations &&
        node._Associations.Kodplan._Associations &&
        node._Associations.Kodplan._Associations.Kontodelsdef;

      const formatter = findFormatter('TEDFormatCurr');

      let konteringar = pipe(
        pathOr<IKontering[]>([], ['_Associations', 'Konteringar'])
        // map(rensaNollDebetKredit)
      )(nodeData);
      konteringar = konteringar.map(kontering => {
        return {
          ...kontering,
          DEBET: formatter.displayer.display(
            kontering.DEBET,
            true,
            'DisplayZeroAsBlank="Y"'
          ),
          KREDIT: formatter.displayer.display(
            kontering.KREDIT,
            true,
            'DisplayZeroAsBlank="Y"'
          ),
        };
      });
      const columns: IColumnDef[] = filtreraAnvändaKoddelar(
        konteringar,
        setKolumnBenamningar(kontoKolumner, kontodelsdef)
      ).map(setCellTitles(kontoKolumner));

      return <ObjectTable columns={columns} data={konteringar} />;
    }}
  </Query>
);

const konteringar: IObjektVy = {
  type: 'ObjektVy',
  id: 'konteringar',
  info: {},
  def: () => ({
    caption: 'Konteringar',
    comp: konteringscomp,
  }),
};
const verifikationsdata = objektVyLista(
  'verifikationsdata',
  [
    'VER_NR',
    'DATUM',
    objektLänk('Anvandare'),
    objektLänk('Bokfgrupp'),
    columnDefVy({
      attributeDef: 'Bokforingsjournal.STATUS',
      caption: 'Status',
    }),
    'TEXT',
    'VERKAT_KOD',
  ],
  'Verifikation'
);

const verifikationssida: IObjektVy = ObjektVySida('forstaSida', [
  [
    objektVyGrupp('first', [verifikationsdata], ' '),
    objektVyGrupp('second', [konteringar], ' '),
  ],
]);
const standard: IObjektVy = standardVy([verifikationssida]);

export const Bokfvervyer: IObjektVy[] = [
  standard,
  verifikationsdata,
  konteringar,
];
